/**
 * Painel lateral do contato (Atendimento): abre ao clicar no topo da conversa e reúne,
 * sem sair do chat, o que o atendente precisa — perfil e contato, ações rápidas
 * (CRM, novo pedido, orçamento, resposta pronta), negociações, pedidos anteriores,
 * orçamentos e tarefas do MESMO cliente.
 *
 * Tudo vem dos datasets que as outras telas já usam (`CrmDataset`, pedidos unificados):
 * nenhum dado é criado aqui. Fonte ausente é dita ("não disponível neste ambiente"), nunca
 * vira lista vazia. A view é pura (testável por SSR); o wrapper carrega os dados
 * só quando o painel abre.
 */
// Import explícito de `React` — ver o mesmo comentário em `layout/AppShell.tsx`.
import React, { type ReactElement, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ClipboardList, FileText, Handshake, ShoppingCart, UserRound, Zap } from "lucide-react";
import {
  crmCustomerById,
  identityPhoneKey,
  isCrmSourceAwaitingContract,
  opportunitiesOfCustomer,
  quotesOfCustomer,
  resolveCrmCustomer,
  tasksOfCustomer,
  type CrmDataset
} from "../crm/dataset.js";
import { LEAD_STAGE_LABELS, QUOTE_STATUS_LABELS, TASK_KIND_LABELS } from "../crm/pipeline.js";
import { formatBrl, formatCount, formatDateTime } from "../format.js";
import { isOrdersSourceAwaitingContract, type OrdersDataset } from "../orders/dataset.js";
import { ORDER_STATE_LABELS } from "../orders/state-machine.js";
import { useChannelConversationActions, useCrmDataset, useOrdersDataset, useQuickReplies } from "../queries.js";
import type { PlatformConversation, PlatformUnifiedOrder, QuickReply } from "../api/types.js";
import { Avatar, Badge, Button, Drawer, buttonClass } from "../ui/index.js";

/** Pedidos do cliente: pelo cadastro canônico OU pelo telefone da conversa (venda do WhatsApp sem vínculo ainda). */
export function ordersOfContact(orders: PlatformUnifiedOrder[], customerId: string | null, phone: string | null): PlatformUnifiedOrder[] {
  const key = identityPhoneKey(phone);
  return orders
    .filter((order) => (customerId !== null && order.customerId === customerId) || (key !== "" && identityPhoneKey(order.customer?.phone) === key))
    .sort((a, b) => b.placedAt.localeCompare(a.placedAt));
}

function Section({ icon: Icon, title, count, children }: { icon: typeof UserRound; title: string; count?: number; children: ReactNode }): ReactElement {
  return (
    <section className="flex flex-col gap-3 border-t border-border pt-5" data-drawer-section={title}>
      <h3 className="flex items-center gap-2 text-base font-extrabold text-ink">
        <Icon className="h-4 w-4 text-muted" aria-hidden focusable={false} />
        {title}
        {count === undefined ? null : <span className="rounded-pill bg-surface-soft px-2 py-0.5 text-xs font-extrabold tabular-nums text-body">{count}</span>}
      </h3>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="m-0 min-w-0 break-words text-right font-bold text-ink">{children}</dd>
    </div>
  );
}

const MUTED = <p className="m-0 text-sm text-muted">Nada por aqui ainda.</p>;
const UNAVAILABLE = <p className="m-0 text-sm text-muted">Não disponível neste ambiente.</p>;

export interface ContactDrawerViewProps {
  conversation: PlatformConversation;
  crm: CrmDataset | null;
  orders: OrdersDataset | null;
  quickReplies: QuickReply[];
  now?: Date;
  /** Dispara uma resposta pronta ao cliente (só com o atendimento humano ativo). */
  onSendQuickReply?: (reply: QuickReply) => void;
  sending?: boolean;
  /** Assumir/devolver à IA (o mesmo controle da caixa de resposta). */
  onToggleMode?: () => void;
  onExport?: () => void;
}

export function ContactDrawerView({ conversation, crm, orders, quickReplies, onSendQuickReply, sending = false, onToggleMode, onExport }: ContactDrawerViewProps): ReactElement {
  const crmReady = crm !== null && !isCrmSourceAwaitingContract(crm, "customers");
  const customer = crmReady ? (crmCustomerById(crm, conversation.customerId) ?? resolveCrmCustomer(crm, { customerId: conversation.customerId, phone: conversation.phone })) : null;
  const customerId = customer?.id ?? null;
  const opportunities = crm && customerId && !isCrmSourceAwaitingContract(crm, "opportunities") ? opportunitiesOfCustomer(crm, customerId) : null;
  const quotes = crm && customerId && !isCrmSourceAwaitingContract(crm, "quotes") ? quotesOfCustomer(crm, customerId) : null;
  const tasks = crm && customerId && !isCrmSourceAwaitingContract(crm, "tasks") ? tasksOfCustomer(crm, customerId) : null;
  const pastOrders = orders && !isOrdersSourceAwaitingContract(orders) ? ordersOfContact(orders.orders, customerId, conversation.phone) : null;
  const human = conversation.mode === "human";
  const whatsapp = conversation.channel === "whatsapp";
  const name = conversation.displayName || customer?.name || conversation.customerId;
  const crmHref = `/clientes/${customerId ?? conversation.customerId}`;

  return (
    <div className="flex flex-col gap-5" data-contact-drawer={conversation.id}>
      <div className="flex items-center gap-4">
        <Avatar name={name} />
        <div className="flex min-w-0 flex-col gap-1">
          <p className="m-0 truncate text-xl font-black text-ink">{name}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone={whatsapp ? "success" : "neutral"}>{whatsapp ? "WhatsApp" : "Chat da loja"}</Badge>
            <Badge tone={human ? "warning" : "info"}>{human ? "Humano" : "IA"}</Badge>
            {customer ? <Badge tone="neutral">Cliente no CRM</Badge> : <Badge tone="warning">Sem cadastro no CRM</Badge>}
          </div>
        </div>
      </div>

      <Section icon={UserRound} title="Contato">
        <dl className="m-0">
          <Row label="Telefone">{conversation.phone ?? customer?.phone ?? "—"}</Row>
          <Row label="E-mail">{customer?.email ?? "—"}</Row>
          <Row label="Origem do cadastro">{customer?.origin ?? "—"}</Row>
          <Row label="Cliente desde">{customer ? formatDateTime(customer.createdAt) : "—"}</Row>
          <Row label="Lead score">{formatCount(conversation.leadScore)}</Row>
          <Row label="Mensagens na conversa">{formatCount(conversation.messages.length)}</Row>
        </dl>
      </Section>

      <Section icon={Zap} title="Ações rápidas">
        <div className="grid grid-cols-2 gap-2" data-drawer-actions="true">
          <Link to={crmHref} className={buttonClass("outline", "md", "w-full")}>
            <UserRound className="h-4 w-4" aria-hidden focusable={false} /> Abrir no CRM
          </Link>
          <Link to="/televendas" className={buttonClass("outline", "md", "w-full")}>
            <ShoppingCart className="h-4 w-4" aria-hidden focusable={false} /> Novo pedido
          </Link>
          <Link to="/crm/orcamentos" className={buttonClass("outline", "md", "w-full")}>
            <FileText className="h-4 w-4" aria-hidden focusable={false} /> Orçamentos
          </Link>
          <Link to="/crm/pipeline" className={buttonClass("outline", "md", "w-full")}>
            <Handshake className="h-4 w-4" aria-hidden focusable={false} /> Pipeline
          </Link>
          {whatsapp && onToggleMode ? (
            <Button className="w-full" onClick={onToggleMode} data-action={human ? "resume" : "takeover"}>
              {human ? "Devolver para a IA" : "Assumir atendimento"}
            </Button>
          ) : null}
          {onExport ? (
            <Button className="w-full" onClick={onExport}>
              Exportar conversa
            </Button>
          ) : null}
        </div>
        {whatsapp ? (
          <div className="flex flex-col gap-2" data-drawer-quick-replies="true">
            <p className="m-0 text-sm font-extrabold text-ink">Disparar resposta pronta</p>
            {!human ? (
              <p className="m-0 text-xs text-muted">Assuma o atendimento para enviar mensagens ao cliente.</p>
            ) : quickReplies.length === 0 ? (
              <p className="m-0 text-xs text-muted">Nenhuma resposta rápida cadastrada (em Relacionamento → Respostas rápidas).</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {quickReplies.map((reply) => (
                  <li key={reply.id} className="flex items-start justify-between gap-3 rounded-control border border-border bg-surface-soft p-3">
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-sm font-extrabold text-ink">{`/${reply.shortcut} · ${reply.title}`}</span>
                      <span className="line-clamp-2 text-xs text-muted">{reply.body}</span>
                    </span>
                    <Button size="sm" variant="primary" disabled={sending || !onSendQuickReply} onClick={() => onSendQuickReply?.(reply)} data-quick-reply={reply.id}>
                      Enviar
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </Section>

      <Section icon={Handshake} title="Negociações" count={opportunities?.length}>
        {opportunities === null ? (
          crmReady && !customerId ? <p className="m-0 text-sm text-muted">Sem cadastro no CRM: nenhuma negociação vinculada.</p> : UNAVAILABLE
        ) : opportunities.length === 0 ? (
          MUTED
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {opportunities.map((opportunity) => (
              <li key={opportunity.id} data-opportunity-id={opportunity.id} className="flex items-center justify-between gap-3 rounded-control border border-border p-3">
                <span className="flex min-w-0 flex-col">
                  <Link to={`/crm/pipeline?opportunity=${encodeURIComponent(opportunity.id)}`} className="truncate text-sm font-extrabold text-ink hover:underline">
                    {opportunity.title}
                  </Link>
                  <span className="text-xs text-muted">{opportunity.valueCents === null ? "sem valor declarado" : formatBrl(opportunity.valueCents)}</span>
                </span>
                <Badge tone={opportunity.stage === "ganho" ? "success" : opportunity.stage === "perdido" ? "danger" : "info"}>{LEAD_STAGE_LABELS[opportunity.stage]}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section icon={ShoppingCart} title="Pedidos anteriores" count={pastOrders?.length}>
        {pastOrders === null ? (
          UNAVAILABLE
        ) : pastOrders.length === 0 ? (
          MUTED
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {pastOrders.map((order) => (
              <li key={order.id} data-order-id={order.id} className="flex items-center justify-between gap-3 rounded-control border border-border p-3">
                <span className="flex min-w-0 flex-col">
                  <Link to={`/pedidos/${order.id}`} className="truncate text-sm font-extrabold text-ink hover:underline">
                    {order.externalId || order.id}
                  </Link>
                  <span className="text-xs text-muted">{`${formatDateTime(order.placedAt)} · ${order.totalCents === null ? "frete a cotar" : formatBrl(order.totalCents)}`}</span>
                </span>
                <Badge tone={order.state === "delivered" ? "success" : order.state === "cancelled" || order.state === "returned" ? "danger" : "info"}>{ORDER_STATE_LABELS[order.state]}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section icon={FileText} title="Orçamentos" count={quotes?.length}>
        {quotes === null ? (
          UNAVAILABLE
        ) : quotes.length === 0 ? (
          MUTED
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {quotes.map((quote) => (
              <li key={quote.id} className="flex items-center justify-between gap-3 rounded-control border border-border p-3 text-sm">
                <Link to={`/crm/orcamentos?quote=${encodeURIComponent(quote.id)}`} className="font-extrabold text-ink hover:underline">
                  {quote.number}
                </Link>
                <span className="text-xs text-muted">{(QUOTE_STATUS_LABELS as Record<string, string>)[quote.status] ?? quote.status}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section icon={CalendarClock} title="Tarefas e follow-ups" count={tasks?.length}>
        {tasks === null ? (
          UNAVAILABLE
        ) : tasks.length === 0 ? (
          MUTED
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {tasks.map((task) => (
              <li key={task.id} className="flex items-center justify-between gap-3 rounded-control border border-border p-3 text-sm">
                <span className="flex min-w-0 flex-col">
                  <Link to={`/crm/tarefas?task=${encodeURIComponent(task.id)}`} className="truncate font-extrabold text-ink hover:underline">
                    {task.title}
                  </Link>
                  <span className="text-xs text-muted">{`${TASK_KIND_LABELS[task.kind]} · prazo ${formatDateTime(task.dueAt)}`}</span>
                </span>
                <Badge tone={task.status === "open" ? "warning" : "neutral"}>{task.status === "open" ? "Aberta" : task.status === "done" ? "Concluída" : "Cancelada"}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Link to={crmHref} className={buttonClass("primary", "md", "w-full")}>
        <ClipboardList className="h-4 w-4" aria-hidden focusable={false} /> Ver ficha completa do cliente (CRM 360)
      </Link>
    </div>
  );
}

/** Carrega CRM, pedidos e respostas rápidas só enquanto o painel está aberto. */
export function ContactDrawer({
  conversation,
  open,
  onClose,
  onExport
}: {
  conversation: PlatformConversation;
  open: boolean;
  onClose: () => void;
  onExport?: () => void;
}): ReactElement | null {
  if (!open) return null;
  return <ContactDrawerLoaded conversation={conversation} onClose={onClose} onExport={onExport} />;
}

function ContactDrawerLoaded({ conversation, onClose, onExport }: { conversation: PlatformConversation; onClose: () => void; onExport?: () => void }): ReactElement {
  const crm = useCrmDataset();
  const orders = useOrdersDataset();
  const quickReplies = useQuickReplies();
  const { send, setMode } = useChannelConversationActions(conversation.id);
  const human = conversation.mode === "human";
  return (
    <Drawer open title={`Contato · ${conversation.displayName || conversation.customerId}`} onClose={onClose}>
      <ContactDrawerView
        conversation={conversation}
        crm={crm.data ?? null}
        orders={orders.data ?? null}
        quickReplies={quickReplies.data ?? []}
        sending={send.isPending}
        onSendQuickReply={(reply) => send.mutate(reply.body)}
        onToggleMode={() => setMode.mutate(human ? "resume" : "takeover")}
        onExport={onExport}
      />
      {send.error ? <p role="alert" className="mt-3 text-sm text-danger">{`Não foi possível enviar: ${send.error.message}`}</p> : null}
      {send.isSuccess ? <p role="status" className="mt-3 text-sm font-bold text-success">Resposta enviada.</p> : null}
    </Drawer>
  );
}
